import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux-hooks';
import useStorage from '../hooks/useStorage';
import { setChatData, setChatLoading } from '../store/chat.slice';
import AgentService from '../services/agent.service';
import { TChatMsg } from '../types/chat.types';
import MarkdownRenderer from './markdown-renderer/MarkdownRenderer';
import { ChatLoader } from './common/ChatLoader';
import { map } from 'lodash';

const StreamMsg = () => {
  const { chatData, chatLoading } = useAppSelector((state) => state.chat);
  const { agentInfo, userInfo } = useAppSelector((state) => state.common);
  const { getItem, setItem } = useStorage();
  const [streamedMsg, setStreamedMsg] = useState('');
  const lastChatMsg = chatData[chatData.length - 1];
  const dispatch = useAppDispatch();

  const saveUserChat = (ques: string, answer: string, msgId: string) => {
    const prevChatData: TChatMsg[] = JSON.parse(getItem('chatData') || '[]');

    prevChatData[prevChatData.length - 1] = {
      ques: lastChatMsg.ques,
      answer,
      model: lastChatMsg.model,
      msgId: lastChatMsg.msgId,
      chatId: lastChatMsg.chatId,
      assistantId: lastChatMsg.assistantId,
      replyText: lastChatMsg.replyText,
    };

    setItem('chatData', JSON.stringify(prevChatData));
    const updatedChat: TChatMsg[] = [...chatData]; // shallow copy
    updatedChat[updatedChat.length - 1] = {
      ques: lastChatMsg.ques,
      answer,
      model: lastChatMsg.model,
      msgId: lastChatMsg.msgId,
      chatId: lastChatMsg.chatId,
      assistantId: lastChatMsg.assistantId,
      replyText: lastChatMsg.replyText,
    };
    dispatch(setChatData(updatedChat));
  };

  const streamResponse = async (response: Response) => {
    if (!lastChatMsg?.msgId) return;
    try {
      setStreamedMsg('');
      dispatch(setChatLoading(false));
      const reader = response && response.body && response.body.getReader();
      const decoder = new TextDecoder();
      let streamedContent = '';
      while (true) {
        const { value, done } = await reader!.read();
        if (done) {
          break;
        }
        const decodedChunk = decoder.decode(value, { stream: true });
        // streamedContent = streamedContent + removeCitations(decodedChunk);
        streamedContent = streamedContent + decodedChunk;
        setStreamedMsg(streamedContent);
      }
      saveUserChat(lastChatMsg.ques, streamedContent, lastChatMsg.msgId);
      setStreamedMsg('');
    } catch (err) {
      saveUserChat(
        lastChatMsg.ques,
        'No response received. Please resend your message.',
        lastChatMsg.msgId
      );
      dispatch(setChatLoading(false));
    }
  };

  const chatMessages = useMemo(() => {
    const chatInfo: TChatMsg[] = JSON.parse(getItem('chatData') || '[]');
    const chatMsgs: TChatMsg[] = chatData.length > 0 ? chatData : chatInfo;
    const msgs: { role: string; content: string; files: never[] }[] = [];
    map(chatMsgs, (item) => {
      if (item.ques) {
        msgs.push({ role: 'user', content: item.ques, files: [] });
      }
      if (item.answer) {
        msgs.push({ role: 'assistant', content: item.answer, files: [] });
      }
    });
    return msgs;
  }, [getItem, chatData]);

  const chatWithAgent = async () => {
    try {
      dispatch(setChatLoading(true));
      const threadId = getItem('threadId') || '';
      const response = await AgentService.addMsgsToThread({
        orgId: agentInfo?.createdByOrg._id || '',
        userQuery: lastChatMsg.ques,
        assistantId: agentInfo?._id || '',
        openaiAssistant: agentInfo?._id || '',
        threadId,
        messages: chatMessages,
        msgId: lastChatMsg.msgId || '',
        replyText: lastChatMsg.replyText || '',
        userId: userInfo?._id || '',
      });
      if (!response.ok || !response.body) {
        throw response.statusText;
      }
      streamResponse(response);
      return;
    } catch (err) {
      saveUserChat(
        lastChatMsg.ques,
        'No response received. Please resend your message.',
        lastChatMsg?.msgId
      );
      dispatch(setChatLoading(false));
    }
  };

  useEffect(() => {
    if (!lastChatMsg.answer) {
      chatWithAgent();
    }
  }, [lastChatMsg]);

  return (
    <div className="w-full">
      {streamedMsg && <MarkdownRenderer markdownText={streamedMsg} />}
      {chatLoading && !streamedMsg && <ChatLoader />}
    </div>
  );
};

export default StreamMsg;
